import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button, Group, Modal, Select, Text, Textarea, TextInput } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";

import { IssuePayoutPayload, useIssuePayout } from "@hooks/mutations/payouts/useIssuePayout";


interface IssuePayoutModalProps {
  opened: boolean;
  onClose: () => void;
  staffId: string | null;
  staffName: string | null;
  amount: number;
  paymentCount: number;
  dateRange: string;
  startDate?: string;
  endDate?: string;
}

export const IssuePayoutModal = ({
  opened,
  onClose,
  staffId,
  staffName,
  amount,
  paymentCount,
  dateRange,
  startDate,
  endDate,
}: IssuePayoutModalProps) => {
  const { t, i18n } = useTranslation();
  const { mutate: issuePayout, isLoading } = useIssuePayout();

  const form = useForm({
    initialValues: {
      payoutMethod: "bank_transfer",
      transactionDate: new Date(),
      notes: "",
      bankName: "",
      bankAccountNumber: "",
      mobileMoneyNumber: "",
    },
    validate: {
      payoutMethod: (value) => (value.trim().length > 0 ? null : t("required-field")),
      transactionDate: (value) => (value ? null : t("required-field")),
      bankName: (value, values) => 
        values.payoutMethod === "bank_transfer" && !value ? t("required-field") : null,
      bankAccountNumber: (value, values) => 
        values.payoutMethod === "bank_transfer" && !value ? t("required-field") : null,
      mobileMoneyNumber: (value, values) => 
        values.payoutMethod === "mobile_money" && !value ? t("required-field") : null,
    },
  });

  // Reset form when modal closes or opens
  useEffect(() => {
    if (opened) {
      form.reset();
    }
  }, [opened]);

  const handleSubmit = (values: typeof form.values) => {
    if (!staffId) return;

    const payload: IssuePayoutPayload = {
      recipient_id: staffId,
      amount: amount,
      payout_method: values.payoutMethod,
      status: "completed",
      description: values.notes,
      processed_at: values.transactionDate.toISOString(),
      dateRange,
      startDate,
      endDate,
      bank_name: values.payoutMethod === "bank_transfer" ? values.bankName : undefined,
      bank_account_number: values.payoutMethod === "bank_transfer" ? values.bankAccountNumber : undefined,
      mobile_money_number: values.payoutMethod === "mobile_money" ? values.mobileMoneyNumber : undefined,
    };

    issuePayout(payload, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text fw={600}>{t("issue-payout")}</Text>}
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Group mb="md" gap="xs">
          <Text size="sm" c="dimmed">{t("staff")}:</Text>
          <Text size="sm" fw={500}>{staffName || t("all")}</Text>
        </Group>

        <Group mb="xl" gap="xs">
          <Text size="sm" c="dimmed">{t("amount")}:</Text>
          <Text size="sm" fw={600} c="orange.7">
            {Intl.NumberFormat(i18n.language, {
              style: "currency",
              currency: "CFA",
            }).format(amount)}
          </Text>
        </Group>

        <Text size="sm" c="orange.7" mb="md">
          This records {paymentCount} eligible commission{paymentCount === 1 ? "" : "s"} as already paid.
          It will not send a bank or mobile money transfer.
        </Text>
        
        <DatePickerInput
          label={t("transaction-date")}
          placeholder={t("select")}
          valueFormat="DD MMM YYYY"
          mb="md"
          withAsterisk
          clearable={false}
          {...form.getInputProps("transactionDate")}
        />

        <Select
          label={t("payout-method")}
          placeholder={t("select")}
          data={[
            { label: "Bank Transfer", value: "bank_transfer" },
            { label: "Mobile Money", value: "mobile_money" },
            { label: "Cash", value: "cash" },
            { label: "Check", value: "check" },
          ]}
          mb="md"
          withAsterisk
          {...form.getInputProps("payoutMethod")}
        />

        {form.values.payoutMethod === "bank_transfer" && (
          <>
            <TextInput
              label={t("bank-name")}
              placeholder={t("enter-bank-name")}
              mb="md"
              withAsterisk
              {...form.getInputProps("bankName")}
            />
            <TextInput
              label={t("account-number")}
              placeholder={t("enter-account-number")}
              mb="md"
              withAsterisk
              {...form.getInputProps("bankAccountNumber")}
            />
          </>
        )}

        {form.values.payoutMethod === "mobile_money" && (
          <TextInput
            label={t("mobile-money-number")}
            placeholder={t("enter-mobile-money-number")}
            mb="md"
            withAsterisk
            {...form.getInputProps("mobileMoneyNumber")}
          />
        )}

        <Textarea
          label={t("notes")}
          placeholder={t("type-notes")}
          minRows={3}
          mb="xl"
          {...form.getInputProps("notes")}
        />

        <Group justify="flex-end">
          <Button variant="subtle" color="gray" onClick={onClose} disabled={isLoading}>
            {t("cancel")}
          </Button>
          <Button type="submit" loading={isLoading}>
            {t("submit")}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};
